import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileDown, Loader2, CheckCircle, XCircle, ArrowLeft, Eye, Share2, Printer, QrCode, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Layout from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DownloadPDFPage = () => {
  const [searchParams] = useSearchParams();
  const includeDiary = searchParams.get('include_diary') === 'true';
  const { language } = useLanguage();
  const navigate = useNavigate();
  const printFrameRef = useRef(null);
  
  const [status, setStatus] = useState('loading'); // loading, ready, viewing, error
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [error, setError] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generatingShare, setGeneratingShare] = useState(false);

  useEffect(() => {
    generatePDF();
  }, []);

  const generatePDF = async () => {
    try {
      setStatus('loading');
      
      const token = localStorage.getItem('sehatSetu_token');
      if (!token) {
        setError('Please login first');
        setStatus('error');
        return;
      }

      const response = await axios.get(
        `${API_URL}/export/pdf?include_diary=${includeDiary}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'arraybuffer'
        }
      );

      // Convert to base64 data URL for embedding
      const base64 = btoa(
        new Uint8Array(response.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ''
        )
      );
      const dataUrl = `data:application/pdf;base64,${base64}`;
      setPdfDataUrl(dataUrl);
      setStatus('ready');
      
    } catch (err) {
      console.error('PDF generation error:', err);
      setError(err.response?.data?.detail || 'Could not generate PDF');
      setStatus('error');
    }
  };

  const handleViewPDF = () => {
    setStatus('viewing');
  };

  const handleDownload = () => {
    if (!pdfDataUrl) return;
    
    // Try direct download
    try {
      const link = document.createElement('a');
      link.href = pdfDataUrl;
      link.download = 'EmergencyCard_Health_Report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(language === 'hi' ? 'PDF डाउनलोड हो रहा है!' : 'PDF downloading!');
    } catch (err) {
      // Fallback - show viewer
      toast.info(language === 'hi' ? 'PDF खोल रहे हैं' : 'Opening PDF');
      setStatus('viewing');
    }
  };

  const handlePrint = () => {
    if (!pdfDataUrl) return;
    
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    if (isIOS) {
      // On iOS, open the PDF in a new window for native print
      // iOS Safari can print from the share menu when viewing a PDF
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>EmergencyCard Health Report - Print</title>
            <style>
              body { margin: 0; padding: 0; }
              iframe { width: 100vw; height: 100vh; border: none; }
              .print-hint {
                position: fixed;
                bottom: 20px;
                left: 50%;
                transform: translateX(-50%);
                background: #0D2340;
                color: white;
                padding: 12px 24px;
                border-radius: 25px;
                font-family: system-ui, -apple-system, sans-serif;
                font-size: 14px;
                z-index: 1000;
                box-shadow: 0 4px 12px rgba(0,0,0,0.3);
              }
            </style>
          </head>
          <body>
            <iframe src="${pdfDataUrl}"></iframe>
            <div class="print-hint">
              Tap Share (box with arrow) → Print
            </div>
          </body>
          </html>
        `);
        printWindow.document.close();
        toast.info(language === 'hi' 
          ? 'Share बटन (↑) दबाकर Print चुनें' 
          : 'Tap Share button (↑) then Print');
      } else {
        toast.error(language === 'hi' ? 'पॉपअप ब्लॉक है' : 'Popup blocked - please allow popups');
      }
    } else {
      // Desktop browsers - use iframe print
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      printFrame.src = pdfDataUrl;
      
      printFrame.onload = () => {
        setTimeout(() => {
          try {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
          } catch (e) {
            // Fallback - open in new tab
            window.open(pdfDataUrl, '_blank');
            toast.info('Print from the new tab');
          }
        }, 500);
      };
      
      document.body.appendChild(printFrame);
      
      // Clean up after printing
      setTimeout(() => {
        if (document.body.contains(printFrame)) {
          document.body.removeChild(printFrame);
        }
      }, 60000);
      
      toast.info(language === 'hi' ? 'प्रिंट डायलॉग खुल रहा है...' : 'Opening print dialog...');
    }
  };

  const handleShare = async () => {
    setGeneratingShare(true);
    setShowShareDialog(true);
    
    try {
      const token = localStorage.getItem('sehatSetu_token');
      
      // Generate a temporary share link via backend
      const response = await axios.post(
        `${API_URL}/export/share-pdf`,
        { include_diary: includeDiary, expiry_hours: 24 },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setShareData(response.data);
    } catch (err) {
      console.error('Share generation error:', err);
      toast.error(language === 'hi' ? 'शेयर लिंक नहीं बन पाया' : 'Could not generate share link');
      setShowShareDialog(false);
    } finally {
      setGeneratingShare(false);
    }
  };

  const copyToClipboard = async () => {
    if (!shareData?.share_url) return;
    
    try {
      await navigator.clipboard.writeText(shareData.share_url);
      setCopied(true);
      toast.success(language === 'hi' ? 'लिंक कॉपी हो गया!' : 'Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy');
    }
  };

  return (
    <Layout>
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
        
        {status === 'loading' && (
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-[#0D2340] mb-2">
                {language === 'hi' ? 'PDF बना रहे हैं...' : 'Generating PDF...'}
              </h2>
              <p className="text-slate-500">
                {language === 'hi' ? 'कृपया प्रतीक्षा करें' : 'Please wait'}
              </p>
            </CardContent>
          </Card>
        )}

        {status === 'ready' && (
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-[#0D2340] mb-2">
                {language === 'hi' ? 'PDF तैयार है!' : 'PDF Ready!'}
              </h2>
              <p className="text-slate-500 mb-6">
                {language === 'hi' 
                  ? 'आपकी हेल्थ रिपोर्ट तैयार है।'
                  : 'Your health report is ready.'}
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={handleViewPDF}
                  className="bg-[#0D2340] hover:bg-[#0D2340]/90"
                  data-testid="view-pdf-btn"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {language === 'hi' ? 'देखें' : 'View'}
                </Button>
                
                <Button 
                  onClick={handleDownload}
                  variant="outline"
                  data-testid="download-pdf-btn"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  {language === 'hi' ? 'Download' : 'Download'}
                </Button>
                
                <Button 
                  onClick={handleShare}
                  variant="outline"
                  data-testid="share-pdf-btn"
                >
                  <QrCode className="h-4 w-4 mr-2" />
                  {language === 'hi' ? 'Share' : 'Share'}
                </Button>
                
                <Button 
                  onClick={handlePrint}
                  variant="outline"
                  data-testid="print-pdf-btn"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  {language === 'hi' ? 'Print' : 'Print'}
                </Button>
              </div>

              <Button 
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="mt-6"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {language === 'hi' ? 'वापस जाएं' : 'Go Back'}
              </Button>
            </CardContent>
          </Card>
        )}

        {status === 'viewing' && (
          <div className="w-full max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <Button 
                onClick={() => setStatus('ready')}
                variant="outline"
                size="sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {language === 'hi' ? 'वापस' : 'Back'}
              </Button>
              
              <div className="flex gap-2">
                <Button onClick={handleShare} size="sm" variant="outline">
                  <QrCode className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Share</span>
                </Button>
                <Button onClick={handlePrint} size="sm" variant="outline">
                  <Printer className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Print</span>
                </Button>
                <Button onClick={handleDownload} size="sm">
                  <FileDown className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Save</span>
                </Button>
              </div>
            </div>
            
            {/* PDF Viewer - embedded */}
            <Card className="overflow-hidden">
              <iframe
                src={pdfDataUrl}
                className="w-full h-[70vh] border-0"
                title="Health Report PDF"
              />
            </Card>
          </div>
        )}

        {status === 'error' && (
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-red-600 mb-2">
                {language === 'hi' ? 'कुछ गड़बड़ हो गई' : 'Something went wrong'}
              </h2>
              <p className="text-slate-500 mb-6">{error}</p>
              
              <Button 
                onClick={generatePDF}
                variant="outline"
                className="w-full"
              >
                {language === 'hi' ? 'फिर से कोशिश करें' : 'Try Again'}
              </Button>

              <Button 
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="mt-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {language === 'hi' ? 'वापस जाएं' : 'Go Back'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Share Dialog with QR Code */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-center">
                {language === 'hi' ? 'PDF शेयर करें' : 'Share PDF'}
              </DialogTitle>
            </DialogHeader>
            
            {generatingShare ? (
              <div className="py-8 text-center">
                <Loader2 className="h-8 w-8 mx-auto animate-spin text-[#0D2340]" />
                <p className="mt-4 text-slate-500">
                  {language === 'hi' ? 'QR कोड बना रहे हैं...' : 'Generating QR code...'}
                </p>
              </div>
            ) : shareData ? (
              <div className="text-center space-y-4">
                {/* QR Code */}
                <div className="bg-white p-4 rounded-lg inline-block mx-auto border">
                  <img 
                    src={`data:image/png;base64,${shareData.qr_code}`} 
                    alt="QR Code" 
                    className="w-48 h-48 mx-auto"
                  />
                </div>
                
                <p className="text-sm text-slate-500">
                  {language === 'hi' 
                    ? `यह लिंक ${shareData.expiry_hours || 24} घंटे के लिए वैध है`
                    : `This link is valid for ${shareData.expiry_hours || 24} hours`}
                </p>
                
                {/* Copy Link */}
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    value={shareData.share_url || ''} 
                    readOnly 
                    className="flex-1 px-3 py-2 text-sm border rounded-lg bg-slate-50 truncate"
                  />
                  <Button onClick={copyToClipboard} size="sm" variant="outline">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                
                <p className="text-xs text-slate-400">
                  {language === 'hi' 
                    ? 'QR स्कैन करें या लिंक शेयर करें'
                    : 'Scan QR or share the link'}
                </p>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DownloadPDFPage;
